import {Suspense, useEffect, useState} from 'react';
import {Avatar, Box} from '@mui/material';
import {Canvas} from '@react-three/fiber';
import {OrbitControls, useGLTF} from '@react-three/drei';

function Model({ url }) {
  const { scene } = useGLTF(url);
  
  useEffect(() => {
    scene.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [scene]);

  return <primitive object={scene} scale={3} position={[0, -4.8, 0]} rotation={[0, 0, 0]} />;
}

export default function UserAvatar({ 
  src, 
  alt, 
  fallbackText, 
  size = 40,
  withBorder = true,
  hasRpmAvatar = false,
  rpmModelUrl,
  sx,
  ...props 
}) {
  const [imgError, setImgError] = useState(false);
  const [validatedSrc, setValidatedSrc] = useState(src);

  useEffect(() => {
    const validatePhotoURL = (url) => {
      if (!url) return null;

      // RPM URLs immer akzeptieren
      if (hasRpmAvatar && url.includes('readyplayer.me')) {
        return url;
      }

      try {
        new URL(url);
        return url;
      } catch (e) {
        return null;
      }
    };

    const validUrl = validatePhotoURL(src);
    
    if (validUrl !== validatedSrc) {
      setImgError(false);
      setValidatedSrc(validUrl);
    }
  }, [src, validatedSrc, hasRpmAvatar]);
  
  const getFallbackText = () => {
    if (fallbackText) return fallbackText;
    if (alt) return alt.charAt(0).toUpperCase();
    return '?';
  };

  const getRandomColor = (text) => {
    const colors = [
      '#082b63', // Sehr Dunkelblau
      '#0d3311', // Sehr Dunkelgrün
      '#7a0000', // Sehr Dunkelrot
      '#2a0066', // Sehr Dunkellila
      '#560027', // Sehr Dunkelpink
      '#002f6c', // Sehr Dunkelhellblau
      '#962800', // Sehr Dunkelorange
      '#13191c'  // Sehr Dunkelblaugrau
    ];
    const index = text.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const showFallback = imgError || !validatedSrc;

  const containerStyles = {
    position: 'relative',
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    overflow: 'hidden',
    bgcolor: 'rgba(0, 0, 0, 0.1)',
    border: withBorder ? '2px solid var(--color-secondary)' : 'none',
    ...sx
  };

  const avatarStyles = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }
  };
  
  // Wenn der Benutzer einen RPM-Avatar hat
  if (hasRpmAvatar) {
    // Nur für große Avatare (z.B. Profilseite) verwende 3D-Modell
    if (size >= 180 && rpmModelUrl) {
      return (
        <Box sx={containerStyles} {...props}>
          <Box sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0
          }}>
            <Canvas
              camera={{ position: [0, 0, 4], fov: 25 }}
              shadows
            >
              <ambientLight intensity={0.5} />
              <directionalLight
                position={[10, 10, 5]}
                intensity={1}
                castShadow
              />
              <Suspense fallback={null}>
                <Model url={rpmModelUrl} />
              </Suspense>
              <OrbitControls
                enableZoom={false}
                enablePan={false}
                minPolarAngle={Math.PI / 2}
                maxPolarAngle={Math.PI / 2}
              />
            </Canvas>
          </Box>
        </Box>
      );
    }
    
    // Für alle anderen Größen verwende das PNG-Portrait
    return (
      <Box sx={containerStyles} {...props}>
        <Avatar
          src={validatedSrc}
          alt={alt}
          imgProps={{
            onLoad: () => setImgError(false),
            referrerPolicy: "no-referrer",
            crossOrigin: "anonymous"
          }}
          onError={() => setImgError(true)}
          className="user-avatar"
          sx={avatarStyles}
        />
      </Box>
    );
  }

  // Fallback für normale Avatare oder wenn kein Bild vorhanden ist
  return (
    <Box sx={containerStyles} {...props}>
      <Avatar
        src={showFallback ? undefined : validatedSrc}
        alt={alt}
        imgProps={{
          onLoad: () => setImgError(false),
          referrerPolicy: "no-referrer",
          crossOrigin: "anonymous"
        }}
        onError={() => setImgError(true)}
        className={`user-avatar ${showFallback ? 'fallback' : ''}`}
        sx={{
          ...avatarStyles,
          bgcolor: getRandomColor(getFallbackText()),
          fontSize: size * 0.4
        }}
      >
        {showFallback && getFallbackText()}
      </Avatar>
    </Box>
  );
} 